import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Circle } from 'lucide-react';
import {
  type OnlineHistoryPoint,
  type OnlineUser,
  useOnlineHistory,
  useOnlineUsers,
} from '@/hooks/useDashboardAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface OnlineUsersWidgetProps {
  limit?: number;
  className?: string;
}

const CHART_AXIS_TICK_COLOR = '#e5e7eb';

interface ChartPoint {
  timestamp: number;
  time: string;
  dateTime: string;
  count: number;
}

interface TooltipPayloadItem {
  value: number;
  payload: ChartPoint;
}

interface OnlineHistoryTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

interface DisplayUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  subtitle: string;
}

function formatChartTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatChartDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildChartData(points: OnlineHistoryPoint[]): ChartPoint[] {
  return points.map((point) => ({
    timestamp: point.timestamp,
    time: formatChartTime(point.timestamp),
    dateTime: formatChartDateTime(point.timestamp),
    count: point.count,
  }));
}

function buildCurrentUserItems(users: OnlineUser[]): DisplayUser[] {
  return users.map((user) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    username: user.username,
    photoUrl: user.photoUrl,
    subtitle: formatLastActivity(user.lastActivity),
  }));
}

function OnlineHistoryTooltip({ active, payload, label }: OnlineHistoryTooltipProps) {
  if (!active || !payload || payload.length === 0) {return null;}
  const point = payload[0].payload;
  return (
    <div className="rounded-md border bg-background px-2.5 py-2 text-xs shadow-sm">
      <p className="font-medium">{point.dateTime ?? label}</p>
      <p className="text-muted-foreground">
        Онлайн: <span className="font-medium text-foreground">{payload[0].value}</span>
      </p>
    </div>
  );
}

function getInitials(
  firstName: string | null,
  lastName: string | null,
  username: string | null,
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {return firstName.slice(0, 2).toUpperCase();}
  if (username) {return username.slice(0, 2).toUpperCase();}
  return 'U';
}

function getDisplayName(user: DisplayUser): string {
  if (user.firstName) {
    return `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
  }
  if (user.username) {
    return `@${user.username}`;
  }
  return `Пользователь #${user.id}`;
}

function formatLastActivity(lastActivity: string): string {
  try {
    return formatDistanceToNow(new Date(lastActivity), { addSuffix: true, locale: ru });
  } catch {
    return 'недавно';
  }
}

function OnlineUsersList({ users, emptyText }: { users: DisplayUser[]; emptyText: string }) {
  if (users.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-3">
        {emptyText}
      </p>
    );
  }

  return (
    <ScrollArea className="h-[260px]">
      <div className="space-y-3">
        {users.map((user) => (
          <div key={user.id} className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.photoUrl ?? undefined} />
                <AvatarFallback className="text-xs">
                  {getInitials(user.firstName, user.lastName, user.username)}
                </AvatarFallback>
              </Avatar>
              <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {getDisplayName(user)}
              </p>
              <p className="text-xs text-muted-foreground">
                {user.subtitle}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

export function OnlineUsersWidget({ limit = 10, className }: OnlineUsersWidgetProps) {
  const { data, isLoading } = useOnlineUsers(limit);
  const { data: historyData } = useOnlineHistory(24);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Онлайн</CardTitle>
          <Skeleton className="h-6 w-12" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={`online-skel-${i}`} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const count = data?.count ?? 0;
  const users = data?.users ?? [];
  const historyPoints = historyData?.data ?? [];
  const chartData = buildChartData(historyPoints);
  const now = Date.now();
  const dayAgo = now - 24 * 60 * 60 * 1000;
  const visibleUsers = buildCurrentUserItems(users);

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Онлайн
        </CardTitle>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Circle className="h-2 w-2 fill-green-500 text-green-500" />
          {count}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        {chartData.length > 0 && (
          <div className="h-[96px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 4, right: 6, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="onlineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                  opacity={0.35}
                />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={[dayAgo, now]}
                  tickFormatter={formatChartTime}
                  axisLine={false}
                  tickLine={false}
                  tickCount={4}
                  minTickGap={18}
                  tick={{ fontSize: 10, fill: CHART_AXIS_TICK_COLOR }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={28}
                  tick={{ fontSize: 10, fill: CHART_AXIS_TICK_COLOR }}
                />
                <Tooltip
                  content={<OnlineHistoryTooltip />}
                  cursor={{ stroke: '#22c55e', strokeOpacity: 0.35 }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#22c55e"
                  strokeWidth={1.5}
                  fill="url(#onlineGradient)"
                  dot={false}
                  activeDot={{ r: 3, fill: '#22c55e' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground">Сейчас онлайн</p>
        </div>
        <OnlineUsersList users={visibleUsers} emptyText="Нет пользователей онлайн" />
      </CardContent>
    </Card>
  );
}
